// src/util/DateUtil.ts

const MINUTE_IN_SECONDS = 60;
const HOUR_IN_SECONDS = 60 * MINUTE_IN_SECONDS;
const DAY_IN_SECONDS = 24 * HOUR_IN_SECONDS;
const MONTH_IN_SECONDS = 30 * DAY_IN_SECONDS; // 근사치
const YEAR_IN_SECONDS = 12 * MONTH_IN_SECONDS;

/**
 * 주어진 ISO 날짜 문자열을 "time ago" 형식으로 변환합니다.
 * @param dateString - ISO 8601 형식의 날짜 문자열 (e.g., "2024-07-05T10:00:00.000Z")
 * @returns 상대적인 시간 문자열 (e.g., "5분 전", "3시간 전")
 */
export const formatTimeAgo = (dateString: string): string => {
  const now = new Date();
  const past = new Date(dateString);
  const secondsAgo = Math.round((now.getTime() - past.getTime()) / 1000);

  if (secondsAgo < 0) {
    return '방금 전'; // 미래 시간은 방금 전으로 처리
  }
  if (secondsAgo < MINUTE_IN_SECONDS) {
    return `${secondsAgo}초 전`;
  }
  if (secondsAgo < HOUR_IN_SECONDS) {
    return `${Math.floor(secondsAgo / MINUTE_IN_SECONDS)}분 전`;
  }
  if (secondsAgo < DAY_IN_SECONDS) {
    return `${Math.floor(secondsAgo / HOUR_IN_SECONDS)}시간 전`;
  }
  if (secondsAgo < MONTH_IN_SECONDS) {
    return `${Math.floor(secondsAgo / DAY_IN_SECONDS)}일 전`;
  }
  if (secondsAgo < YEAR_IN_SECONDS) {
    return `${Math.floor(secondsAgo / MONTH_IN_SECONDS)}달 전`;
  }

  return `${Math.floor(secondsAgo / YEAR_IN_SECONDS)}년 전`;
};

/**
 * 설문 남은 시간 계산
 *
 * - 설문 종료시간을 기준으로 남은 시간 계산
 * - X일 X시간 남음 등으로 나타냄
 * - 일 / 시간 / 분 단위로 표시
 */
export const remainingQuestionTime = (endDate: string): string => {
  const now = new Date();
  const end = new Date(endDate);
  const remainingSeconds = Math.max(0, Math.round((end.getTime() - now.getTime()) / 1000));

  const days = Math.floor(remainingSeconds / DAY_IN_SECONDS);
  const hours = Math.floor((remainingSeconds % DAY_IN_SECONDS) / HOUR_IN_SECONDS);
  const minutes = Math.floor((remainingSeconds % HOUR_IN_SECONDS) / MINUTE_IN_SECONDS);
  let result = '';
  if (days > 0) {
    result += `${days}일 `;
  }
  if (hours > 0 || days > 0) {
    result += `${hours}시간 `;
  }
  if (minutes > 0 || hours > 0 || days > 0) {
    result += `${minutes}분 `;
  }

  // 최종 결과에 "남음" 추가
  result += '남음';
  return result.trim() || '';
};

/**
 * 설문 종료 일자 계산 함수

 * 선택한 presetDuration 값에 따라 설문 종료 일자를 계산하여
 * 2025년 12월 31일 12시 00분 형식으로 반환
 */
export const finishQuestionDate = (presetDuration: number): string => {
  const now = new Date();
  const end = new Date(now.getTime() + presetDuration * 60 * 60 * 1000); // 시간을 밀리초로 변환
  const year = end.getFullYear();
  const month = String(end.getMonth() + 1).padStart(2, '0');
  const day = String(end.getDate()).padStart(2, '0');
  const hours = String(end.getHours()).padStart(2, '0');
  const minutes = String(end.getMinutes()).padStart(2, '0');

  return `${year}년 ${month}월 ${day}일 ${hours}시 ${minutes}분`;
};
